import { Inbox, MessageSquare, UsersRound, X } from 'lucide-react'
import { type ReactNode } from 'react'

import { markdownToPlainText } from '@aeris/communication'
import {
  Avatar,
  cn,
  EmptyState,
  LoadFailed,
  Pagination,
  SearchInput,
  Select,
  Spinner,
} from '@aeris/ui'

import type {
  ConversationFilterType,
  ConversationSummaryType,
  InboxAccountType,
} from '../lib/api.ts'
import {
  contactHasName,
  contactLabelOf,
  contactPhoneOf,
  contactSeedOf,
  shortTimeOf,
} from '../lib/inbox.ts'
import { useContactNames } from '../lib/use-contact-names.ts'

import { mediaLabelOf } from './inbox-message.tsx'

/**
 * A coluna de conversas — F085.
 *
 * ## Abaixo de `lg`, ela é a TELA
 *
 * Três colunas num tablet de 900px dão 300px cada, e nenhuma das três serve:
 * a lista trunca todo nome, a conversa fica mais estreita do que era antes
 * desta feature, e o painel de detalhes vira uma tira. Então abaixo de `lg`
 * não há três colunas — há navegação: a lista ocupa a tela, escolher uma
 * conversa a substitui pela thread, e um botão volta.
 *
 * ## O que ela é, e o que ela deixou de ser
 *
 * Era um cartão flutuante de altura fixa (`h-[34rem]`) com borda, cantos
 * arredondados e um "10 conversas" no topo. Virou uma coluna: largura fixa,
 * altura inteira, separada do resto por uma régua de 1px.
 *
 * O contador de conversas saiu. Ele ocupava uma linha permanente para dizer um
 * número que ninguém usa para decidir nada — e o pedido do dono foi explícito
 * sobre isso: *"não precisa existir título Conversas, a busca já deixa claro o
 * propósito da coluna"*.
 *
 * ## Só a lista rola
 *
 * Busca e filtros ficam presos no topo. É a diferença entre procurar um
 * cliente e ter de rolar de volta para cima para procurar o próximo — numa
 * tela que um atendente deixa aberta o dia inteiro, isso acontece cem vezes.
 */

/** Os quatro recortes, e o que cada um responde. Todos saem de dado que existe (F084). */
const FILTERS: readonly {
  value: ConversationFilterType | null
  label: string
}[] = [
  { value: null, label: 'Todas' },
  { value: 'unread', label: 'Não lidas' },
  { value: 'waiting', label: 'Aguardando' },
  { value: 'mine', label: 'Minhas' },
]

export type InboxSidebarProps = {
  /** Escondida abaixo de `lg` quando há conversa aberta — ver a doc do arquivo. */
  hiddenOnSmall: boolean
  rows: readonly ConversationSummaryType[]
  total: number
  isPending: boolean
  isError: boolean
  page: number
  onPageChange: (page: number) => void
  pageSize: number
  /** "Tentar de novo" quando a lista não carregou (F132). */
  onRetry?: () => void
  selectedId: string | null
  onSelect: (conversation: ConversationSummaryType) => void
  search: string
  onSearchChange: (search: string) => void
  filter: ConversationFilterType | null
  onFilterChange: (filter: ConversationFilterType | null) => void
  /**
   * Os números da loja (F091). Com menos de dois, o seletor não aparece —
   * ver o comentário no corpo.
   */
  accounts: readonly InboxAccountType[]
  /** `null` é "todos os números". */
  accountId: string | null
  onAccountChange: (accountId: string | null) => void
  unreadTotal: number
  footer?: ReactNode
}

/**
 * A prévia da última mensagem.
 *
 * Texto PLANO pelo mesmo parser da bolha: um `replace` ingênuo comeria o `2*3`
 * de um orçamento e a lista discordaria da conversa aberta ao lado (F083).
 *
 * Sem corpo, o tipo responde (F085). Uma conversa cuja última mensagem é uma
 * foto tem `lastMessagePreview` nulo — e a lista dizia "Sem mensagens" para
 * uma conversa que acabara de receber uma. "Sem mensagens" ficou para o que é
 * de fato vazio: uma conversa criada e ainda sem nada dentro.
 */
const previewOf = (conversation: ConversationSummaryType): string => {
  if (conversation.lastMessagePreview !== null) {
    return markdownToPlainText(conversation.lastMessagePreview)
  }
  if (conversation.lastMessageKind !== null) {
    return mediaLabelOf(conversation.lastMessageKind)
  }
  return 'Sem mensagens'
}

const ConversationRow = ({
  conversation,
  selected,
  onSelect,
  showChannel,
  ownName,
}: {
  conversation: ConversationSummaryType
  /** O nome que este atendente deu ao contato, só para ele (01/10). */
  ownName: string | null
  selected: boolean
  onSelect: (conversation: ConversationSummaryType) => void
  /**
   * Só quando a lista mostra TODOS os números (F091). Escolhido um, todas as
   * linhas são dele, e repetir o nome em cada uma seria gastar a única coluna
   * estreita da tela para dizer o que o seletor logo acima já diz.
   */
  showChannel: boolean
}): ReactNode => (
  <li>
    <button
      type="button"
      onClick={() => {
        onSelect(conversation)
      }}
      aria-current={selected ? 'true' : undefined}
      className={cn(
        'relative flex w-full items-center gap-2.5 px-3 py-2.5 text-left',
        'transition-colors duration-(--duration-fast)',
        // A régua entre linhas é o divisor, não uma borda em volta de cada
        // uma: um cartão por conversa transforma a lista numa pilha de caixas.
        'border-b border-(--border-subtle)',
        selected
          ? // Seleção discreta: fundo levemente tingido e uma barra fina na
            // lateral. Uma borda azul em volta do item lê como "campo com
            // foco", não como "esta é a conversa aberta".
            'bg-(--brand-primary-subtle) before:absolute before:inset-y-0 before:left-0 before:w-0.5 before:bg-(--brand-primary)'
          : 'hover:bg-(--surface-hover)'
      )}
    >
      {/*
       * A cor vem do `wa_id`, não do nome: o nome de um contato muda quando o
       * perfil dele muda, e a cor não deve mudar junto — é por ela que o olho
       * reencontra alguém numa lista de cem.
       */}
      <Avatar
        label={contactLabelOf(conversation, ownName)}
        seed={contactSeedOf(conversation)}
        size="md"
      />

      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-baseline justify-between gap-2">
          <span className="flex min-w-0 items-center gap-1 text-(length:--text-sm) font-(--weight-medium) text-(--text-primary)">
            {conversation.contactKind === 'group' ? (
              <UsersRound
                aria-label="Grupo"
                className="h-3.5 w-3.5 shrink-0 text-(--text-secondary)"
              />
            ) : null}
            <span className="truncate">
              {contactHasName(conversation, ownName)
                ? contactLabelOf(conversation, ownName)
                : contactPhoneOf(conversation)}
            </span>
          </span>
          <span className="shrink-0 text-(length:--text-xs) text-(--text-muted)">
            {conversation.lastMessageAt === null
              ? ''
              : shortTimeOf(conversation.lastMessageAt)}
          </span>
        </span>

        <span className="flex items-center justify-between gap-2">
          <span className="flex min-w-0 flex-1 items-center gap-1.5">
            {showChannel ? (
              /*
               * O nome do número, na mesma linha da prévia e ANTES dela.
               *
               * `shrink-0` e `max-w-24`: a prévia é que cede espaço, porque o
               * canal responde "isto é meu?" e a prévia responde "sobre o
               * quê?" — a primeira decide se a segunda vale a leitura. Mas com
               * um teto, senão um nome longo empurraria a prévia para fora.
               */
              <span className="flex max-w-24 shrink-0 items-center gap-0.5 rounded-(--radius-full) bg-(--surface-sunken) px-1.5 text-(length:--text-xs) text-(--text-secondary)">
                <MessageSquare
                  aria-hidden="true"
                  className="h-2.5 w-2.5 shrink-0"
                />
                <span className="sr-only">
                  Recebida pelo WhatsApp {conversation.accountDisplayName}.{' '}
                </span>
                <span aria-hidden="true" className="truncate">
                  {conversation.accountDisplayName}
                </span>
              </span>
            ) : null}
            <span className="min-w-0 truncate text-(length:--text-xs) text-(--text-muted)">
              {previewOf(conversation)}
            </span>
          </span>
          {conversation.unreadCount > 0 ? (
            /*
             * O crachá é um círculo com o número, não um `Badge` de texto: ele
             * aparece em muitas linhas ao mesmo tempo e precisa ser lido pela
             * FORMA, de canto de olho, sem competir com o nome ao lado.
             */
            <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-(--radius-full) bg-(--brand-primary) px-1.5 text-(length:--text-xs) font-(--weight-medium) text-(--text-on-brand)">
              {conversation.unreadCount}
              <span className="sr-only"> mensagens não lidas</span>
            </span>
          ) : null}
        </span>
      </span>
    </button>
  </li>
)

export const InboxSidebar = ({
  rows,
  total,
  isPending,
  isError,
  page,
  onPageChange,
  pageSize,
  selectedId,
  onSelect,
  search,
  onSearchChange,
  filter,
  onFilterChange,
  accounts,
  accountId,
  onAccountChange,
  unreadTotal,
  footer,
  hiddenOnSmall,
  onRetry,
}: InboxSidebarProps): ReactNode => {
  /*
   * O seletor só existe com DOIS números ou mais (F091).
   *
   * Um `<select>` de uma opção não é uma escolha — é um controle que não faz
   * nada, ocupando uma linha da única coluna estreita desta tela. A loja com
   * um número só nunca vê que este código existe, e é assim que tem de ser:
   * a feature nasceu porque uma loja passou a ter dois.
   */
  const showAccounts = accounts.length > 1
  const contactNames = useContactNames()

  return (
    <section
      aria-label="Conversas"
      className={cn(
        'min-h-0 w-full flex-1 flex-col border-(--border-default)',
        'lg:flex lg:w-80 lg:flex-none lg:border-r',
        hiddenOnSmall ? 'hidden' : 'flex'
      )}
    >
      <div className="flex shrink-0 flex-col gap-2 border-b border-(--border-default) px-3 py-2.5">
        {showAccounts ? (
          <Select
            aria-label="Mostrar conversas de qual número"
            value={accountId ?? ''}
            onChange={(event) => {
              onAccountChange(
                event.target.value === '' ? null : event.target.value
              )
            }}
            className="h-9 text-(length:--text-sm)"
          >
            {/*
             * "Todos" é a primeira opção e o padrão de quem nunca escolheu:
             * esconder conversa por omissão é como um inbox perde mensagem.
             */}
            <option value="">Todos os números</option>
            {accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.displayName}
              </option>
            ))}
          </Select>
        ) : null}

        {/* A busca do kit (F132), na altura compacta da coluna. O "x" nativo
            do WebKit não segue o tema; o nosso fica ao lado, com rótulo. */}
        <div className="relative">
          <SearchInput
            value={search}
            onChange={(event) => {
              onSearchChange(event.target.value)
            }}
            placeholder="Buscar conversa ou telefone"
            aria-label="Buscar conversa ou telefone"
            wrapperClassName="min-w-0"
            className="h-9 bg-(--surface-sunken) pr-8 [&::-webkit-search-cancel-button]:hidden"
          />
          {search === '' ? null : (
            <button
              type="button"
              aria-label="Limpar busca"
              onClick={() => {
                onSearchChange('')
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-(--radius-sm) p-0.5 text-(--text-muted) hover:text-(--text-primary)"
            >
              <X aria-hidden="true" className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <div
          role="group"
          aria-label="Filtrar conversas"
          className="flex flex-wrap gap-1"
        >
          {FILTERS.map((option) => {
            const active = option.value === filter
            return (
              <button
                key={option.label}
                type="button"
                aria-pressed={active}
                onClick={() => {
                  onFilterChange(option.value)
                }}
                className={cn(
                  'flex items-center gap-1 rounded-(--radius-full) px-2.5 py-1',
                  'text-(length:--text-xs) font-(--weight-medium)',
                  'transition-colors duration-(--duration-fast)',
                  active
                    ? 'bg-(--brand-primary) text-(--text-on-brand)'
                    : 'bg-(--surface-sunken) text-(--text-secondary) hover:bg-(--surface-hover)'
                )}
              >
                {option.label}
                {/*
                 * O número só aparece no chip de não lidas, e só quando há
                 * alguma. Um "0" permanente ao lado de "Não lidas" é ruído; e
                 * inventar contagem para os outros três exigiria uma consulta
                 * por chip para responder algo que ninguém perguntou.
                 */}
                {option.value === 'unread' && unreadTotal > 0 ? (
                  <span
                    className={cn(
                      'rounded-(--radius-full) px-1 text-(length:--text-xs)',
                      active
                        ? 'bg-white/25'
                        : 'bg-(--brand-primary) text-(--text-on-brand)'
                    )}
                  >
                    {unreadTotal}
                  </span>
                ) : null}
              </button>
            )
          })}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isPending ? (
          <div className="flex justify-center py-8">
            <Spinner label="Carregando as conversas" />
          </div>
        ) : isError ? (
          <LoadFailed
            what="as conversas"
            {...(onRetry === undefined ? {} : { onRetry })}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Inbox aria-hidden="true" className="h-8 w-8" />}
            title={
              search === '' && filter === null && accountId === null
                ? 'Nenhuma conversa ainda'
                : /*
                   * Um número escolhido e nada nele é uma frase diferente de
                   * "nada com esse recorte": o número EXISTE, está conectado, e
                   * ninguém escreveu para ele. Dizer "nada com esse recorte"
                   * aqui manda a pessoa procurar um filtro errado que ela não
                   * aplicou.
                   */
                  accountId !== null && search === '' && filter === null
                  ? 'Nenhuma conversa neste número'
                  : 'Nada com esse recorte'
            }
            description={
              search === '' && filter === null && accountId === null
                ? 'Uma conversa aparece aqui assim que um cliente manda uma mensagem para o número conectado.'
                : accountId !== null && search === '' && filter === null
                  ? 'Ninguém escreveu para este número ainda. Escolha "Todos os números" para ver o resto.'
                  : 'Tente outro termo, ou volte para "Todas".'
            }
          />
        ) : (
          <ul className="flex flex-col">
            {rows.map((conversation) => (
              <ConversationRow
                key={conversation.id}
                conversation={conversation}
                selected={conversation.id === selectedId}
                onSelect={onSelect}
                showChannel={showAccounts && accountId === null}
                ownName={contactNames.nameOf(conversation.contactId)}
              />
            ))}
          </ul>
        )}
      </div>

      {/* A paginação do kit (F132): some sozinha quando cabe numa página. */}
      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={onPageChange}
        label="Paginação das conversas"
        className="shrink-0 justify-between border-t border-(--border-default) px-3 py-2"
      />

      {/*
       * O atalho do guia mora aqui embaixo, e não no topo da tela.
       *
       * Ele é útil enquanto a configuração não terminou — e some sozinho
       * quando os dez passos estão feitos. Mas uma faixa no topo do espaço de
       * trabalho custa uma linha de altura para TODA conversa lida ali dentro,
       * para sempre. No rodapé da coluna ele continua alcançável e não come a
       * área de leitura.
       */}
      {footer === undefined ? null : (
        <div className="shrink-0 border-t border-(--border-default) px-3 py-2">
          {footer}
        </div>
      )}
    </section>
  )
}
